import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpDownIcon, ChevronDownIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { CandidaciesSortKeyAndOrder } from "@/types";

const jobPipelineSortBySchema = z.object({
  sortKeyAndOrder: z.nativeEnum(CandidaciesSortKeyAndOrder, {
    required_error: "Required",
  }),
});

export const JobPipelineSortBy = ({
  disabled,
  isInDefaultState,
  setSort,
  sort,
}: {
  disabled?: boolean;
  isInDefaultState: boolean;
  setSort: (sort: CandidaciesSortKeyAndOrder) => void;
  sort: CandidaciesSortKeyAndOrder;
}) => {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof jobPipelineSortBySchema>>({
    defaultValues: {
      sortKeyAndOrder: sort,
    },
    resolver: zodResolver(jobPipelineSortBySchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't
    // applied the sort, so reset the form to the current sort.
    if (!open) {
      form.reset({ sortKeyAndOrder: sort });
    }
  };

  const handleSubmit = (data: z.infer<typeof jobPipelineSortBySchema>) => {
    setSort(data.sortKeyAndOrder);
    setOpen(false);
  };

  // Need to reset the form when the sort changes as a result of global reset.
  useEffect(() => {
    form.reset({ sortKeyAndOrder: sort });
  }, [form, sort]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger disabled={disabled} asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <ArrowUpDownIcon className="h-4 w-4" />
          <span>
            <span className="font-semibold">Sort by:</span> {sort}
          </span>
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="sortKeyAndOrder"
              render={({ field }) => (
                <FormItem className="p-4 text-slate-700">
                  <FormLabel>Sort by</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {Object.entries(CandidaciesSortKeyAndOrder).map(
                        ([key, value]) => (
                          <FormItem key={key} className="flex gap-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={value} />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {value}
                            </FormLabel>
                          </FormItem>
                        ),
                      )}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </div>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
